// src/lib/galleryUpload.ts
// Client beacons for the gallery upload funnel, modeled on lib/videoUpload's
// reportUploadEvent. The server audits presign/CreateImage/CommitImage itself;
// these beacons cover ONLY what the server can't see: file choice, local
// validation declines, the browser-owned presigned PUT, and batch halts.

import { apiFetch } from '@/lib/api';

// Server-side step allowlist — anything else 400s.
export type GalleryUploadEventStep =
  | 'GalleryFileSelected'
  | 'GalleryFileRejected'
  | 'GalleryS3Put'
  | 'GalleryBatchHalted';

// "rejected" (policy/validation declined the file) is deliberately distinct
// from "error" (the transfer broke). Collapsing them would make
// GalleryFileRejected useless.
export type GalleryUploadEventOutcome = 'ok' | 'rejected' | 'error';

export interface GalleryUploadEventOpts {
  code?: string;
  durationMs?: number;
  /** File size for per-file beacons; a COUNT for aggregate beacons
   *  (batch_overflow_sliced = sliced files, GalleryBatchHalted = files left). */
  bytesTotal?: number;
  /** Shared id for every beacon in one multi-file pick. */
  batchId?: string;
}

// Best-effort telemetry for the browser-owned phase of a gallery upload.
// `keepalive: true` lets the POST survive the navigation / tab-close that
// often rides along with a failure. Never throws; telemetry must not break
// the upload flow.
export function reportGalleryUploadEvent(
  correlationId: string | undefined,
  step: GalleryUploadEventStep,
  outcome: GalleryUploadEventOutcome,
  opts: GalleryUploadEventOpts = {}
): void {
  if (!correlationId) return;
  try {
    void apiFetch('/api/gallery/upload-event', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        correlationId,
        step,
        outcome,
        code: opts.code ? opts.code.slice(0, 200) : null,
        durationMs: Math.round(opts.durationMs ?? 0),
        bytesTotal: Math.round(opts.bytesTotal ?? 0),
        batchId: opts.batchId ?? null,
      }),
    }).catch(() => {});
  } catch {
    /* swallow — telemetry is best-effort */
  }
}

/** The server now enforces the 12-image cap itself (409, problem type
 *  `gallery_full`) — it can fire on the legacy path and on races the client
 *  slot maths can't see. Distinct error type so the upload error path can
 *  show the friendly message and skip the multipart fallback (which would
 *  just 409 again). */
export class GalleryFullError extends Error {
  constructor(max = 12) {
    super(`This gallery is full (${max} images).`);
    this.name = 'GalleryFullError';
  }
}

/** True when a response is the server's gallery-full conflict. Clones before
 *  reading so the caller can still consume the body. */
export async function isGalleryFullResponse(resp: Response): Promise<boolean> {
  if (resp.status !== 409) return false;
  try {
    const body: any = await resp.clone().json();
    return /gallery_full/i.test(String(body?.type ?? body?.error ?? ''));
  } catch {
    return false;
  }
}
